// Recruiting — a candidate's page. Under the name: tabs (owner, 2026-09-28) —
// Profile (Stage / Outcome / Referred by dropdowns, the Edit / Do not contact /
// Attach link / Merge chips, the facts, applications, files and resume),
// Notes (add a note, call or text; the log), Email (compose; the emails) and
// License check. ./actions/CandidateActions owns the tabs and every write;
// this file owns every read and hands each tab its read-only body.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { useResource, refetchResource } from '../../lib/store';
import { EmptyState, FaultState } from '../FaultState';
import { LOCATION_NAMES, type LocationCode } from '../../lib/locations';
import {
  fetchRecruitingCandidate, recruitingCandidateKey, formatCivilDate,
  STAGE_LABEL, FLAG_LABEL, logKindLabel,
  type CandidateLogEntry, type CandidateFile,
} from '../../lib/recruitingApi';
import {
  CandidateActions, StageField, OutcomeField, ReferredByField, type RecruitingViewer,
} from './actions/CandidateActions';
import { OUTCOMES, OUTCOME_LABEL } from './actions/constants';
import { CandidateFiles, pickResume } from './CandidateFiles';

function outcomeLabel(code: string): string {
  return (OUTCOMES as readonly string[]).includes(code) ? OUTCOME_LABEL[code as (typeof OUTCOMES)[number]] : code;
}

/** What a manager recorded from the Florida DOH site (owner, 2026-09-28). */
const LICENSE_CHECK_LABEL: Record<string, string> = {
  VALID: 'Clear / active',
  EXPIRED: 'Expired or inactive',
  NOT_FOUND: 'Not found',
  DISCIPLINED: 'Discipline on file',
};

const EM_DASH = '—';
const LOG_COLLAPSE_LINES = 8;

function locationName(code: string | null): string {
  if (!code) return EM_DASH;
  return LOCATION_NAMES[code as LocationCode] ?? code;
}

/** A log body past 8 lines collapses, per the build spec — a "more" chip
 * expands it in place, "less" collapses it back. Line breaks preserved
 * (white-space: pre-wrap in CSS), never re-flowed. */
function LogBody({ body }: { body: string }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const lines = body.split('\n');
  const isLong = lines.length > LOG_COLLAPSE_LINES;
  const shown = expanded || !isLong ? body : `${lines.slice(0, LOG_COLLAPSE_LINES).join('\n')}…`;
  return (
    <div>
      <p class="recruiting-log-body">{shown}</p>
      {isLong ? (
        <button type="button" class="btn" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'less' : 'more'}
        </button>
      ) : null}
    </div>
  );
}

function LogEntryRow({ entry }: { entry: CandidateLogEntry }): JSX.Element {
  return (
    <li data-testid={`recruiting-log-${entry.id}`}>
      <div class="section-head-row">
        <div class="empty">
          {formatCivilDate(entry.occurredAt)} — {entry.author || 'Unknown'}
        </div>
        <span class="badge">{logKindLabel(entry.kind)}</span>
      </div>
      {entry.body ? <LogBody body={entry.body} /> : null}
    </li>
  );
}

export function CandidateCard(
  { candidateId, onClose, viewer = null, onChanged, archived = null }: {
    candidateId: number;
    onClose?: () => void;
    /** Who is looking: decides which action chips show (owner-only lift, a
     * manager's own clinic). Null renders the card read-only. */
    viewer?: RecruitingViewer | null;
    /** Called after any write on this candidate, so the board refreshes its
     * counts and rows. */
    onChanged?: () => void;
    /** Which side of the Archive line the card was opened from — decides
     * whether it is kept between visits with the Archive or follows the
     * live pipeline (lib/recruitingApi.ts recruitingCandidateKey). */
    archived?: boolean | null;
  },
): JSX.Element {
  const key = recruitingCandidateKey(candidateId, archived);
  const load = useResource(key, () => fetchRecruitingCandidate(candidateId));

  if (load.status === 'loading') {
    return (
      <section class="card" data-testid="recruiting-card">
        <EmptyState label="Loading candidate…" />
      </section>
    );
  }
  if (load.status === 'fault') {
    return (
      <section class="card" data-testid="recruiting-card">
        <FaultState name="recruiting_candidate_load_failed" detail={load.error} />
      </section>
    );
  }

  const { candidate: c, applications, files, log, possibleDuplicates: possibleDuplicatesRaw } = load.data;
  // Defensive default — never a crash if a mock or an older cached response
  // omits the field entirely (this build's own addition to the response).
  const possibleDuplicates = possibleDuplicatesRaw ?? [];
  // Newest first, ALWAYS — never trust the server's own order (same
  // discipline as People.tsx's NotesSection sorting its own notes rather
  // than assuming the API already did).
  const sortedLog = [...log].sort((a, b) => (
    a.occurredAt < b.occurredAt ? 1 : a.occurredAt > b.occurredAt ? -1 : b.id - a.id
  ));
  const otherFlags = c.flags.filter((f) => f !== 'DO_NOT_CONTACT');
  // First, middle, last (owner, 2026-09-28) — the header and the Name row.
  const fullName = [c.firstName, c.middleName, c.lastName].filter((x) => x && String(x).trim()).join(' ');
  const refresh = (): void => {
    void refetchResource(key, () => fetchRecruitingCandidate(candidateId));
    onChanged?.();
  };
  const emailLog = sortedLog.filter((e) => e.kind === 'EMAIL_OUT' || e.kind === 'EMAIL_IN');

  return (
    <section class="card candidate-page" data-testid="recruiting-card">
      <div class="section-head">
        <div class="section-head-row candidate-head">
          <h2>
            {fullName}
          </h2>
          {viewer ? (
            <div class="candidate-head-status" data-testid="candidate-head-status">
              <StageField key={`stage-${c.stage}`} candidate={c} onChanged={refresh} />
              <OutcomeField key={`outcome-${c.outcome ?? ''}`} candidate={c} onChanged={refresh} viewer={viewer} />
            </div>
          ) : null}
          <div class="section-head-actions">
            <span class="badge" data-testid="recruiting-card-stage">
              {c.outcome ? outcomeLabel(c.outcome) : (STAGE_LABEL[c.stage] ?? c.stage)}
              {!c.outcome && c.stageSince ? ` since ${formatCivilDate(c.stageSince)}` : ''}
            </span>
            {c.doNotContact ? <span class="badge bad">{FLAG_LABEL.DO_NOT_CONTACT}</span> : null}
            {otherFlags.map((f) => <span key={f} class={f === 'IN_PROCESS' ? 'badge ok' : 'badge'}>{FLAG_LABEL[f] ?? f}</span>)}
            {onClose ? <button type="button" class="btn" onClick={onClose}>Close</button> : null}
          </div>
        </div>
      </div>

      {c.doNotContact ? (
        <p class="bad" role="alert">
          Do not contact{c.dncReason ? ` — ${c.dncReason}` : ''}.
        </p>
      ) : null}

      {possibleDuplicates.length > 0 ? (
        <div class="empty" data-testid="recruiting-possible-duplicates" role="note">
          Possible duplicate{possibleDuplicates.length > 1 ? 's' : ''}, sharing {' '}
          {possibleDuplicates.map((d) => d.sharedIdentity.kind.toLowerCase()).filter((v, i, a) => a.indexOf(v) === i).join(', ')}:{' '}
          {possibleDuplicates.map((d, i) => (
            <span key={d.id}>
              {i > 0 ? ', ' : ''}
              <a href={`/recruiting?candidate=${d.id}`}>{d.lastName}, {d.firstName} (#{d.id})</a>
            </span>
          ))}
          . Use Merge (top right) if this is the same person.
        </div>
      ) : null}

      <CandidateActions
        candidate={c}
        viewer={viewer}
        onChanged={refresh}
        licenseLine={(
          <p data-testid="license-check-status">
            License {c.licenseNumber || '(no number on file)'}
            {c.licenseCheckedAt ? (
              <span data-testid="license-checked-line">
                {` — checked on DOH ${formatCivilDate(c.licenseCheckedAt)} by ${c.licenseCheckedBy || 'unknown'}: ${LICENSE_CHECK_LABEL[c.licenseStatus || ''] || c.licenseStatus}`}
                {c.licenseExpires ? `, expires ${formatCivilDate(c.licenseExpires)}` : ''}
                {c.licenseCheckNote ? ` (${c.licenseCheckNote})` : ''}
              </span>
            ) : <span class="muted"> — not yet checked on DOH</span>}
          </p>
        )}
        profileBody={(
          <div class="candidate-columns">
            <div class="candidate-facts">
              {!viewer ? (
                <p data-testid="candidate-status-readonly">
                  Stage: {STAGE_LABEL[c.stage] ?? c.stage}{c.stageSince ? ` since ${formatCivilDate(c.stageSince)}` : ''}
                  {c.outcome ? ` · Outcome: ${outcomeLabel(c.outcome)}${c.outcomeReason ? ` — ${c.outcomeReason}` : ''}` : ''}
                </p>
              ) : null}
              <table>
                <tbody>
                  <tr><th scope="row">Name</th><td data-testid="candidate-full-name">{fullName}</td></tr>
                  <tr>
                    <th scope="row">Email</th>
                    <td>
                      {c.email || c.emailRelay || EM_DASH}
                    </td>
                  </tr>
                  <tr><th scope="row">Phone</th><td>{c.phone || EM_DASH}</td></tr>
                  <tr>
                    <th scope="row">License</th>
                    <td>
                      {c.licenseNumber || EM_DASH}
                      {c.licenseCheckedAt
                        ? <span class="muted">{` — checked ${formatCivilDate(c.licenseCheckedAt)}: ${LICENSE_CHECK_LABEL[c.licenseStatus || ''] || c.licenseStatus}`}</span>
                        : c.licenseNumber ? <span class="muted"> — not yet checked on DOH</span> : null}
                    </td>
                  </tr>
                  <tr><th scope="row">Position</th><td>{c.position || EM_DASH}</td></tr>
                  <tr><th scope="row">Clinic</th><td>{locationName(c.homeLocationCode)}</td></tr>
                  <tr><th scope="row">Hiring manager</th><td>{c.hiringManager || EM_DASH}</td></tr>
                  <tr><th scope="row">Source</th><td>{c.source || EM_DASH}</td></tr>
                  <tr><th scope="row">Received</th><td>{formatCivilDate(c.receivedAt)}</td></tr>
                  {c.stageSince ? <tr><th scope="row">In stage since</th><td>{formatCivilDate(c.stageSince)}</td></tr> : null}
                  <tr>
                    <th scope="row">Referred by</th>
                    <td>
                      {viewer
                        ? <ReferredByField key={`ref-${c.referredByEmployeeId ?? ''}`} candidate={c} onChanged={refresh} />
                        : (c.referredByName || EM_DASH)}
                    </td>
                  </tr>
                  {c.outcome && c.outcomeReason ? <tr><th scope="row">Outcome reason</th><td>{c.outcomeReason}</td></tr> : null}
                </tbody>
              </table>

              <section data-testid="recruiting-applications">
                <h3>Applications</h3>
                {applications.length === 0 ? <EmptyState label="No applications on file." /> : (
                  <ul>
                    {applications.map((a) => (
                      <li key={a.id}>
                        {(a.locationCode ? locationName(a.locationCode) : a.appliedArea) || EM_DASH}
                        {' · '}{formatCivilDate(a.appliedAt)}{' · '}{a.source || EM_DASH}
                        {a.position ? ` · ${a.position}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <CandidateFiles
                candidateId={c.id}
                homeLocationCode={c.homeLocationCode}
                files={files}
                resumeFileId={c.resumeFileId}
                currentResumeFileId={c.currentResumeFileId}
                viewer={viewer}
                onChanged={refresh}
              />
            </div>

            <ResumePreview resume={pickResume(files, c.resumeFileId)} />
          </div>
        )}
        logBody={(
          <section data-testid="recruiting-log">
            <h3>Log</h3>
            {sortedLog.length === 0 ? <EmptyState label="No log entries yet." /> : (
              <ul>
                {sortedLog.map((entry) => <LogEntryRow key={entry.id} entry={entry} />)}
              </ul>
            )}
          </section>
        )}
        emailLogBody={(
          <section data-testid="recruiting-email-log">
            <h3>Emails</h3>
            {emailLog.length === 0 ? <EmptyState label="No emails yet." /> : (
              <ul>
                {emailLog.map((entry) => <LogEntryRow key={entry.id} entry={entry} />)}
              </ul>
            )}
          </section>
        )}
      />
    </section>
  );
}

/** The resume itself, on the page — Google's own viewer for the file in the
 * Recruiting drive (the same thing a Drive link opens, without leaving).
 * Which file: the one marked current, else the newest by a trusted date
 * (server-picked, CandidateFiles.tsx pickResume). A file that is only a link
 * is offered as one; none is said plainly. */
function ResumePreview({ resume }: { resume: CandidateFile | null }): JSX.Element {
  const inDrive = resume && resume.driveFileId ? resume : null;
  const linked = resume && resume.url ? resume : null;
  if (inDrive) {
    const open = inDrive.url ?? `https://drive.google.com/file/d/${inDrive.driveFileId}/view`;
    return (
      <section class="candidate-resume" data-testid="recruiting-resume">
        <div class="section-head-row">
          <h3>Resume{inDrive.isCurrent ? ' (current)' : ''}</h3>
          <a class="doc-link" href={open} target="_blank" rel="noreferrer">Open in Drive</a>
        </div>
        <iframe
          class="resume-frame"
          data-testid="recruiting-resume-frame"
          src={`https://drive.google.com/file/d/${inDrive.driveFileId}/preview`}
          title={`Resume — ${inDrive.name}`}
        />
      </section>
    );
  }
  return (
    <section class="candidate-resume" data-testid="recruiting-resume">
      <h3>Resume{resume?.isCurrent ? ' (current)' : ''}</h3>
      {linked ? (
        <a class="doc-link" href={linked.url as string} target="_blank" rel="noreferrer">{linked.name}</a>
      ) : resume ? (
        <p>{resume.name} <span class="muted">in the Recruiting drive (no link yet)</span></p>
      ) : (
        <EmptyState label="No resume on file." />
      )}
    </section>
  );
}
